/**
 * Storage Utilities
 *
 * Functions for calculating IndexedDB storage usage and formatting sizes
 */

import db from '@/db/db';
import { decorateClient } from '@/db/types/client';
import type { ClientStorageInfo, StorageQuotaInfo } from '../types/storage';

/**
 * Get browser storage quota information
 * Returns null if StorageManager API unavailable
 */
export async function getStorageQuota(): Promise<StorageQuotaInfo | null> {
  if (!navigator.storage || !navigator.storage.estimate) {
    return null;
  }

  try {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage || 0,
      quota: estimate.quota || 0,
      available: true
    };
  } catch (error) {
    console.error('Error getting storage quota:', error);
    return null;
  }
}

/**
 * Calculate rough size of an object in bytes
 * Uses JSON.stringify + Blob to get accurate byte count
 * Adds 10% overhead estimate for encryption
 *
 * Note: This includes all object fields, including:
 * - Document.pdfContent (base64-encoded PDF blobs)
 * - Document.super_form (form data)
 * - Any other nested data structures
 */
export function calculateObjectSize(obj: unknown): number {
  try {
    const jsonString = JSON.stringify(obj);
    const sizeInBytes = new Blob([jsonString]).size;
    // Add 10% overhead for encryption
    return Math.ceil(sizeInBytes * 1.1);
  } catch (error) {
    console.error('Error calculating object size:', error);
    return 0;
  }
}

/**
 * Get storage information for a specific client
 * Calculates total size and breakdown by data type
 */
export async function getClientStorageInfo(
  clientId: number
): Promise<ClientStorageInfo> {
  // Get client and decorate for name
  const client = await db.clients.get(clientId);
  if (!client) {
    throw new Error(`Client ${clientId} not found`);
  }

  const decorated = decorateClient(client);
  const clientName = decorated.fullName;

  // Query all data for this client
  const documents = await db.documents.where('client_id').equals(clientId).toArray();

  // Get signatures for all documents belonging to this client
  const documentIds = documents.map(doc => doc.id).filter((id): id is number | string => id != null);
  const signatures = await db.signatures
    .where('document_id')
    .anyOf(documentIds)
    .toArray();

  // Get contacts for this client (customer_id is the client_id)
  const contacts = await db.contacts.where('customer_id').equals(clientId).toArray();

  // Calculate sizes (includes all fields: super_form data, pdfContent blobs, etc.)
  const documentsSize = documents.reduce((sum, doc) => sum + calculateObjectSize(doc), 0);
  const signaturesSize = signatures.reduce((sum, sig) => sum + calculateObjectSize(sig), 0);
  const contactsSize = contacts.reduce((sum, contact) => sum + calculateObjectSize(contact), 0);

  // Include signatures in total but don't break them out separately (they're tied to documents)
  const totalSize = documentsSize + signaturesSize + contactsSize;

  return {
    clientId,
    clientName,
    totalSize,
    documentsSize,
    documentCount: documents.length,
    contactsSize,
    contactCount: contacts.length
  };
}

/**
 * Get storage information for all clients
 * Returns array sorted by size (largest first)
 */
export async function getAllClientsStorageInfo(): Promise<ClientStorageInfo[]> {
  // Get all clients
  const clients = await db.clients.toArray();

  // Calculate storage for each client
  const storageInfoPromises = clients.map(client =>
    getClientStorageInfo(client.id)
  );

  const storageInfos = await Promise.all(storageInfoPromises);

  // Sort by total size descending
  return storageInfos.sort((a, b) => b.totalSize - a.totalSize);
}

/**
 * Format bytes to human-readable string
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string like "1.2 MB"
 */
export function formatBytes(bytes: number, decimals: number = 1): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Format with decimals, then remove trailing zeros
  const formatted = (bytes / Math.pow(k, i)).toFixed(dm);
  const trimmed = parseFloat(formatted).toString();

  return `${trimmed} ${sizes[i]}`;
}
